import { useState } from 'react';
import { toast } from 'react-toastify';
import { Container } from '../../../../types/canvas.types';
import { getItemDisplayName } from '../utils/containerHelpers';
import { findContainerById } from '../../../../utils/containerSearch';

type DeleteConfirmation = {
  isOpen: boolean;
  type: 'component' | 'nested-container' | null;
  id: string | null;
  containerId: string | null;
  name: string;
};

export interface UseContainerDeleteReturn {
  deleteConfirmation: DeleteConfirmation;
  isDeleting: boolean;
  handleDeleteComponent: (item: any, containerId: string) => void;
  handleDeleteNestedContainer: (nestedContainer: Container) => void;
  confirmDelete: () => Promise<void>;
  cancelDelete: () => void;
}

export const useContainerDelete = (
  selectedContainerId: string | null,
  selectedContainer: Container | null,
  activePageId: string | null,
  containers: Record<string, Container[]>,
  releaseComponent: (componentId: string, containerId: string) => void,
  removeComponentFromContainer: (componentId: string, containerId: string) => void,
  getComponentDefinition: (componentId: string) => any,
  getAllComponentsWithUsageInfo: (containerId: string) => any[],
  removeContainerFromHierarchy: (containerId: string) => void,
  setContainers: React.Dispatch<React.SetStateAction<Record<string, Container[]>>>
): UseContainerDeleteReturn => {
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation>({
    isOpen: false,
    type: null,
    id: null,
    containerId: null,
    name: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteComponent = (item: any, containerId: string) => {
    const displayName = getItemDisplayName(item);
    setDeleteConfirmation({
      isOpen: true,
      type: 'component',
      id: item.id,
      containerId: containerId,
      name: displayName
    });
  };

  const handleDeleteNestedContainer = (nestedContainer: Container) => {
    setDeleteConfirmation({
      isOpen: true,
      type: 'nested-container',
      id: nestedContainer.id,
      containerId: selectedContainerId,
      name: nestedContainer.name
    });
  };

  const removeNestedContainer = (nestedContainerId: string, parentContainerId: string) => {
    if (!activePageId) return;

    const releaseNestedContainerComponents = (containerId: string, containers: Container[]) => {
      const container = findContainerById(containers, containerId);
      if (container) {
        container.content.items?.forEach(item => {
          releaseComponent(item.id, containerId);
        });

        if (container.content.submodel) {
          releaseComponent(container.content.submodel.id, containerId);
        }

        container.content.nestedContainers?.forEach(nc => {
          releaseNestedContainerComponents(nc.id, [nc]);
        });
      }
    };

    if (containers[activePageId]) {
      releaseNestedContainerComponents(nestedContainerId, containers[activePageId]);
    }

    setContainers(prevContainers => {
      if (!prevContainers[activePageId]) return prevContainers;

      const updateContainerTree = (containers: Container[]): Container[] => {
        return containers.map(container => {
          if (container.id === parentContainerId) {
            return {
              ...container,
              content: {
                ...container.content,
                nestedContainers: container.content.nestedContainers?.filter(
                  nc => nc.id !== nestedContainerId
                ) || []
              }
            };
          }

          if (container.content.nestedContainers?.length) {
            return {
              ...container,
              content: {
                ...container.content,
                nestedContainers: updateContainerTree(container.content.nestedContainers)
              }
            };
          }

          return container;
        });
      };

      return {
        ...prevContainers,
        [activePageId]: updateContainerTree(prevContainers[activePageId])
      };
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation.id || !deleteConfirmation.type || !deleteConfirmation.containerId) return;

    setIsDeleting(true);

    try {
      if (deleteConfirmation.type === 'component') {
        releaseComponent(deleteConfirmation.id, deleteConfirmation.containerId);

        removeComponentFromContainer(deleteConfirmation.id, deleteConfirmation.containerId);

        const component = getComponentDefinition(deleteConfirmation.id);
        if (component?.type === 'submodel') {
          const allComponents = getAllComponentsWithUsageInfo(deleteConfirmation.containerId);
          allComponents
            .filter(c => c.parentId === deleteConfirmation.id)
            .forEach(child => {
              releaseComponent(child.id, deleteConfirmation?.containerId ?? '');
            });
        }

        toast.success(`Component "${deleteConfirmation.name}" removed successfully`);
      } else if (deleteConfirmation.type === 'nested-container') {
        if (selectedContainer) {
          const nestedContainer = findContainerById(
            selectedContainer.content.nestedContainers || [],
            deleteConfirmation.id
          );

          if (nestedContainer) {
            nestedContainer.content.items?.forEach(item => {
              releaseComponent(item.id, deleteConfirmation.id ?? '');
            });

            if (nestedContainer.content.submodel) {
              releaseComponent(nestedContainer.content.submodel.id, deleteConfirmation.id ?? '');
            }

            const releaseNestedComponents = (containers: Container[]) => {
              containers.forEach(nc => {
                nc.content.items?.forEach(item => {
                  releaseComponent(item.id, nc.id);
                });
                if (nc.content.submodel) {
                  releaseComponent(nc.content.submodel.id, nc.id);
                }
                if (nc.content.nestedContainers) {
                  releaseNestedComponents(nc.content.nestedContainers);
                }
              });
            };

            if (nestedContainer.content.nestedContainers) {
              releaseNestedComponents(nestedContainer.content.nestedContainers);
            }
          }
        }

        removeContainerFromHierarchy(deleteConfirmation.id);

        removeNestedContainer(deleteConfirmation.id, deleteConfirmation.containerId);

        toast.success(`Container "${deleteConfirmation.name}" removed successfully`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`Failed to delete ${deleteConfirmation.type}`);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmation({ isOpen: false, type: null, id: null, containerId: null, name: '' });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, type: null, id: null, containerId: null, name: '' });
  };

  return {
    deleteConfirmation,
    isDeleting,
    handleDeleteComponent,
    handleDeleteNestedContainer,
    confirmDelete,
    cancelDelete
  };
};
